import React from "react";
import useEnergyHarbourPermitForm from "../../../../hooks/useEnergyHarbourPermitForm";
import LoadingSpinner from "../../../Common/LoadingSpinner";
import PermitHeader from "../PermitHeader";
import {
    CodeStatus,
    HarbourOffice,
    HotWorkSection,
    OrderedBy,
    PermitCategory,
    PermitType,
} from "../../../../common/enums";
import PermitActionBar from "../PermitActionBar";
import RemindMe from "../RemindMe";
import HotWorkCategoryOneOptionsModal from "./HotWorkCategoryOneOptionsModal";
import HotWorkCategoryTwoOptionsModal from "./HotWorkCategoryTwoOptionsModal";
import TextInput from "../../../Common/TextInput";
import TextArea from "../../../Common/TextArea";
import ParticipantsList from "../ParticipantsList";
import { Accordion } from "react-bootstrap";
import HotWorkConditionsAccordionItem from "./HotWorkConditionsAccordionItem";
import ProtectiveEquipment from "../../Conditions/ProtectiveEquipment";
import Measures from "../../Conditions/Measures";
import RiskIdentifications from "../../Conditions/RiskIdentifications";
import DateInput from "../../../Common/DateInput";
import RadioButton from "../../../Common/RadioButton";
import LabelCheckbox from "../../../Common/LabelCheckbox";
import ColumnCheckbox from "../../../Common/ColumnCheckbox";
import Agreements from "./Agreements";
import FinishPermitAccordion from "./FinishPermitAccordion";
import Button from "../../../Common/Button";
import AcceptUnsavedChangesModal from "../AcceptUnsavedChangesModal";
import SignatureDisplay from "../../../Signatures/SignatureDisplay";
import SignatureModal from "../../../Signatures/SignatureModal";
import { PermitFormProps } from "../../../../types/forms/Permits/PermitFormProps";
import HotWorkCategoryThreeOptionsModal from "./HotWorkCategoryThreeOptionsModal";
import ClickableLabelCheckbox from "../../../Common/ClickableLabelCheckbox";
import PositionPicker from "../PositionPicker";
import { HOT_WORK_CATEGORY_TWO_OPTIONS_VERSION_2026_03 } from "../../../../types/permits/EnergyHarbourPermit/HotWorkCategoryTwoOptions";
import CounterSignatureRequestInput from "../CounterSignatureRequestInput";
import { CounterSignatureRequest_isActive } from "../../../../types/CounterSignatureRequest";
import { permitContentVersions } from "../../../../types/WorkPermit";
import Multiselect from "multiselect-react-dropdown";
import { EnergyHarbourCustomer } from "../../../../types/EnergyHarbourCustomer";
import ReadOnlyTextInput from "../../../Common/ReadOnlyTextInput";

export type EnergyHarbourPermitFormProps = PermitFormProps & {
    //
    // divingPermitDocumentId and underWaterWorkPermitId point to permits that should autofill parts of the form.
    // The values are only used for new permits. They have no meaning if the permit already exists.
    //
    divingPermitDocumentId: string | undefined;
    underWaterWorkPermitDocumentId: string | undefined;
    canCreateUnderWaterWorkPermits: boolean;
    canCreateDivingPermits: boolean;
};

function EnergyHarbourPermitForm(props: EnergyHarbourPermitFormProps) {
    const formManager = useEnergyHarbourPermitForm(props);

    if (formManager.isLoading) {
        return <LoadingSpinner />;
    }

    const { resources } = props;
    const { storedPermit, formState, department, formStateChangeHandler } = formManager;

    const alertItems = storedPermit ? storedPermit.alertInfo.map((s) => <li key={s}>{s}</li>) : [];

    const showHotWorkAccordion =
        formManager.showHotWorkConditions ||
        formManager.showHotWorkCatOneConditions ||
        formManager.showHotWorkCatTwoConditions ||
        formManager.showHotWorkCatThreeConditions;

    const showHotWorkSignatures = formState.permitCategories.value.some(
        (pc) =>
            pc === PermitCategory.HotWorkCategoryOne ||
            pc === PermitCategory.HotWorkCategoryTwo ||
            pc === PermitCategory.HotWorkCategoryThree,
    );

    const submitHandler = formManager.canCreate
        ? formManager.submitCreateHandlerAsync
        : formManager.canUpdate
          ? formManager.submitUpdateHandlerAsync
          : formManager.showUpdateHotWorkConditionsButton
            ? formManager.submitUpdateHotWorkHandlerAsync
            : undefined;

    const showCustomerNameSnapshot =
        formState.orderedBy.value === OrderedBy.Other &&
        (storedPermit?.status === CodeStatus.Accepted || storedPermit?.status === CodeStatus.Archived) &&
        !!storedPermit.orderedByCustomerNameSnapshot;

    const showCustomerSelect =
        !showCustomerNameSnapshot &&
        formState.orderedBy.value === OrderedBy.Other &&
        (!storedPermit || storedPermit.contentVersion >= permitContentVersions.version_2026_05);

    const selectedOrderedByCustomer =
        formManager.energyHarbourCustomers.find(
            (c) =>
                c.companyId === formState.orderedByCustomer.value?.companyId &&
                c.companyDepartmentId === formState.orderedByCustomer.value?.companyDepartmentId,
        ) ?? null;

    return (
        <>
            <div className="mt-5 mb-5">
                <div className="container">
                    <PermitHeader
                        formState={formState}
                        permitDocument={storedPermit}
                        permitNameResource={resources.get("EnergyHarbourPermit")}
                        permitType={PermitType.EnergyHarbourPermit}
                        resources={resources}
                    />

                    {storedPermit ? (
                        <PermitActionBar
                            acceptPermitClickHandler={formManager.acceptButtonClickHandler}
                            checkoutPermitClickHandler={formManager.checkoutPermitClickHandlerAsync}
                            copyPermitClickHandler={formManager.copyButtonClickHandler}
                            department={department}
                            permitDocument={storedPermit}
                            renewPermitClickHandler={formManager.renewPermitClickHandlerAsync}
                            resources={props.resources}
                            requestInProgress={formManager.requestInProgress}
                        />
                    ) : null}

                    {alertItems.length > 0 ? (
                        <div>
                            <div className="alert alert-info mt-3">
                                <ul className="m-2">{alertItems}</ul>
                            </div>
                        </div>
                    ) : null}

                    {formManager.showMissingHotWorkSignatureAlert ? (
                        <div>
                            <div className="alert alert-danger mt-3 text-center" style={{ fontSize: "18px" }}>
                                {resources.get("HotWorkSignatureMissing")}
                            </div>
                        </div>
                    ) : null}

                    {formManager.showUnderWaterWorkPermitIdMissing ? (
                        <div className="alert alert-danger mt-3 text-center" style={{ fontSize: "18px" }}>
                            {resources.get("UnderWaterWorkPermitIdMissing")}
                        </div>
                    ) : null}

                    {formManager.showDivingPermitIdMissing ? (
                        <div className="alert alert-danger mt-3 text-center" style={{ fontSize: "18px" }}>
                            {resources.get("DivingPermitIdMissing")}
                        </div>
                    ) : null}

                    <form onSubmit={submitHandler} onKeyDown={formManager.formKeyDownHandler} className="mt-4">
                        <div className="d-flex">
                            <span className="fw-bold me-2">{resources.get("HarbourOffice")}:</span>
                            <RadioButton
                                checked={formState.harbourOffice.value === HarbourOffice.Skarvik}
                                inputId="harbour-office__skarvik"
                                label="Skarvik"
                                onSelect={() =>
                                    formManager.canManageContent
                                        ? formStateChangeHandler("harbourOffice", HarbourOffice.Skarvik)
                                        : false
                                }
                                containerClassName="me-3"
                                name="harbour-office"
                                readOnly={!formManager.canManageContent}
                            />
                            <RadioButton
                                checked={formState.harbourOffice.value === HarbourOffice.Torshamnen}
                                inputId="harbour-office__torshamnen"
                                label="Torshamnen"
                                onSelect={() =>
                                    formManager.canManageContent
                                        ? formStateChangeHandler("harbourOffice", HarbourOffice.Torshamnen)
                                        : false
                                }
                                name="harbour-office"
                                readOnly={!formManager.canManageContent}
                                labelClassName={!formManager.canManageContent ? "pe-none" : ""}
                            />
                        </div>

                        <PositionPicker
                            canManagePositions={formManager.canManagePositions}
                            canManagePositionIsActive={formManager.canManagePositionActiveStatus}
                            canManagePolygons={formManager.canManagePositionPolygons}
                            canManageComments={formManager.canManagePositions}
                            canManageCategories={formManager.canManagePositionCategories}
                            onPositionAdded={formManager.addPositionPickerRowHandler}
                            onPositionChange={formManager.updatePositionPickerRowHandler}
                            onPositionRemoved={formManager.removePositionPickerRowHandler}
                            onUpdatePositionsClicked={formManager.updatePositionsClickHandlerAsync}
                            positions={formState.positions}
                            resources={props.resources}
                            showUpdatePositionsButton={formManager.showUpdatePositionsButton}
                            selectedCategories={formState.permitCategories.value}
                            showActiveStatus={true}
                            showCategories={storedPermit?.supportsPositionCategories ?? true}
                        />

                        <div className="row mt-2">
                            <DateInput
                                defaultValue={formState.startDate.value ?? new Date()}
                                inputId="start-date"
                                label={resources.get("StartDate")}
                                onBlur={formManager.startDateHandler.blurHandler}
                                containerClassName="col-md-6"
                                errorMessage={formState.startDate.errorMessage}
                                readonly={!formManager.canManageStartDate}
                                minDate={formManager.startDateMin ?? undefined}
                                ref={formManager.startDateHandler.ref}
                            />
                            <DateInput
                                defaultValue={formState.endDate.value ?? new Date()}
                                inputId="end-date"
                                label={resources.get("EndDate")}
                                onBlur={formManager.endDateHandler.blurHandler}
                                containerClassName="col-md-6"
                                errorMessage={formState.endDate.errorMessage}
                                readonly={!formManager.canManageEndDate}
                                minDate={formManager.canManageContent ? formState.startDate.value : undefined}
                                ref={formManager.endDateHandler.ref}
                            />
                        </div>

                        <div className="mt-3">
                            <RemindMe
                                formState={formState}
                                onChange={formManager.registerFormStateChange}
                                resources={resources}
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div
                            className="d-flex flex-column flex-lg-row mt-3 justify-content-lg-end"
                            aria-errormessage="permit-category__error-message"
                        >
                            <span className="fw-bold me-2">{resources.get("TypeOfWork")}:</span>
                            <LabelCheckbox
                                checked={formState.permitCategories.value.includes(PermitCategory.ColdWork)}
                                id="type-of-work__cold-work"
                                label={resources.get("ColdWork")}
                                onChange={(checked) =>
                                    checked
                                        ? formManager.permitCategoryAddedHandler(PermitCategory.ColdWork)
                                        : formManager.permitCategoryRemovedHandler(PermitCategory.ColdWork)
                                }
                                containerClassName="me-3"
                                readOnly={!formManager.canManageColdWorkCategory}
                            />
                            <ClickableLabelCheckbox
                                checked={formState.permitCategories.value.includes(PermitCategory.HotWorkCategoryOne)}
                                id="type-of-work__hot-work-category-one"
                                label={resources.get("HotWorkCategoryOne") ?? ""}
                                onChange={(checked) =>
                                    checked
                                        ? formManager.permitCategoryAddedHandler(PermitCategory.HotWorkCategoryOne)
                                        : formManager.permitCategoryRemovedHandler(PermitCategory.HotWorkCategoryOne)
                                }
                                containerClassName="me-3"
                                readOnly={!formManager.canManagePermitCategory}
                                onLabelClick={() => formManager.setShowHotWorkCategoryOneOptionsModal(true)}
                                tooltip={resources.get("HotWorkCatOneTooltip")}
                            />
                            <ClickableLabelCheckbox
                                checked={formState.permitCategories.value.includes(PermitCategory.HotWorkCategoryTwo)}
                                id="type-of-work__hot-work-category-two"
                                label={resources.get("HotWorkCategoryTwo") ?? ""}
                                onChange={(checked) =>
                                    checked
                                        ? formManager.permitCategoryAddedHandler(PermitCategory.HotWorkCategoryTwo)
                                        : formManager.permitCategoryRemovedHandler(PermitCategory.HotWorkCategoryTwo)
                                }
                                readOnly={!formManager.canManagePermitCategory}
                                onLabelClick={() => formManager.setShowHotWorkCategoryTwoOptionsModal(true)}
                                tooltip={resources.get("HotWorkCatTwoTooltip")}
                                containerClassName="me-3"
                            />
                            <ClickableLabelCheckbox
                                checked={formState.permitCategories.value.includes(PermitCategory.HotWorkCategoryThree)}
                                id="type-of-work__hot-work-category-three"
                                label={resources.get("HotWorkCategoryThree") ?? ""}
                                onChange={(checked) =>
                                    checked
                                        ? formManager.permitCategoryAddedHandler(PermitCategory.HotWorkCategoryThree)
                                        : formManager.permitCategoryRemovedHandler(PermitCategory.HotWorkCategoryThree)
                                }
                                readOnly={!formManager.canManagePermitCategory}
                                onLabelClick={() => formManager.setShowHotWorkCategoryThreeOptionsModal(true)}
                                tooltip={resources.get("HotWorkCatThreeTooltip")}
                            />
                        </div>
                        <div id="permit-category__error-message" className="d-flex justify-content-lg-end">
                            <span className="text-danger">{formState.permitCategories.errorMessage}</span>
                        </div>

                        <div
                            className="d-flex flex-column flex-lg-row justify-content-lg-end mt-2"
                            aria-errormessage="ordered-by__error-message"
                        >
                            <span className="fw-bold me-2">{resources.get("OrderedBy")}</span>
                            <RadioButton
                                checked={formState.orderedBy.value === OrderedBy.PortOfGothenburg}
                                inputId="ordered-by__port-of-gothenburg"
                                label={resources.get("PortOfGothenburg")}
                                onSelect={() => formStateChangeHandler("orderedBy", OrderedBy.PortOfGothenburg)}
                                containerClassName="me-3"
                                readOnly={!formManager.canManageOrderedBy}
                                labelClassName={!formManager.canManageOrderedBy ? "pe-none" : ""}
                                name="ordered-by"
                            />
                            <RadioButton
                                checked={formState.orderedBy.value === OrderedBy.Other}
                                inputId="ordered-by__other"
                                label={resources.get("OrderedByOther")}
                                onSelect={() => formStateChangeHandler("orderedBy", OrderedBy.Other)}
                                readOnly={!formManager.canManageOrderedBy}
                                labelClassName={!formManager.canManageOrderedBy ? "pe-none" : ""}
                                name="ordered-by"
                            />
                        </div>
                        {showCustomerNameSnapshot ? (
                            <div className="d-flex justify-content-lg-end mt-2">
                                <ReadOnlyTextInput
                                    labelText={resources.get("OrderedByCustomer")}
                                    value={storedPermit.orderedByCustomerNameSnapshot ?? ""}
                                    inputId="ordered-by__customer-name-snapshot"
                                />
                            </div>
                        ) : showCustomerSelect ? (
                            <div
                                className="d-flex justify-content-lg-end mt-2"
                                aria-errormessage="ordered-by__error-message"
                            >
                                <div style={{ minWidth: "400px" }}>
                                    <label
                                        className="fw-bold form-label mb-0"
                                        htmlFor="ordered-by__customer-select_input"
                                    >
                                        {resources.get("OrderedByCustomer")}
                                    </label>
                                    <Multiselect
                                        style={{
                                            inputField: {
                                                width: "100%",
                                                display: selectedOrderedByCustomer ? "none" : undefined,
                                            },
                                        }}
                                        id="ordered-by__customer-select"
                                        options={formManager.energyHarbourCustomers}
                                        displayValue="name"
                                        selectionLimit={1}
                                        closeOnSelect={true}
                                        disable={!formManager.canManageOrderedBy}
                                        placeholder={resources.get("SelectCustomer")}
                                        hidePlaceholder={!!formState.orderedByCustomer.value?.companyId}
                                        selectedValues={selectedOrderedByCustomer ? [selectedOrderedByCustomer] : []}
                                        onSelect={formManager.handleOrderedByCustomerSelected}
                                        onRemove={formManager.handleOrderedByCustomerRemoved}
                                        onKeyPressFn={(e: KeyboardEvent) => {
                                            if (e.key === "Enter") e.preventDefault();
                                        }}
                                    />
                                </div>
                            </div>
                        ) : null}
                        <div id="ordered-by__error-message" className="d-flex justify-content-lg-end">
                            <span className="text-danger">{formState.orderedBy.errorMessage}</span>
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                value={formState.entrepreneur.value}
                                errorMessage={formState.entrepreneur.errorMessage}
                                inputId="entrepreneur"
                                labelText={props.resources.get("Entrepreneur")}
                                onChange={(s) => formStateChangeHandler("entrepreneur", s)}
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextArea
                                textAreaId="description"
                                value={formState.description.value}
                                errorMessage={formState.description.errorMessage}
                                labelText={props.resources.get("WorkPurposeDescription")}
                                onChange={(s) => formStateChangeHandler("description", s)}
                                readOnly={!formManager.canManageDescription}
                            />
                        </div>

                        {formManager.showUpdateParticipantsButton ? (
                            <Button className="mt-3" onClick={formManager.updateParticipantsClickHandlerAsync}>
                                {resources.get("UpdateParticipants")}
                            </Button>
                        ) : null}

                        <div className="row mt-3">
                            <ColumnCheckbox
                                checked={formState.workLeader.isActive}
                                label={resources.get("OnSite")}
                                onChange={(checked) =>
                                    formManager.personFormElementChangeHandler("workLeader", {
                                        ...formState.workLeader,
                                        isActive: checked,
                                    })
                                }
                                inputId="work-leader__is-active"
                                containerClassName="col-1 d-flex flex-column align-items-center"
                                readOnly={!formManager.canManageParticipants}
                            />
                            <TextInput
                                inputId="work-leader__name"
                                containerClassName="col-md-5"
                                value={formState.workLeader.name}
                                errorMessage={formState.workLeader.nameErrorMessage}
                                labelText={resources.get("WorkLeader")}
                                onChange={(s) =>
                                    formManager.personFormElementChangeHandler("workLeader", {
                                        ...formState.workLeader,
                                        name: s,
                                    })
                                }
                                readOnly={!formManager.canManageContent}
                                inputClassName={formState.workLeader.isActive ? "border border-2 border-success" : ""}
                            />
                            <TextInput
                                inputId="work-leader__phone-number"
                                containerClassName="col-md-5"
                                value={formState.workLeader.phoneNumber}
                                errorMessage={formState.workLeader.phoneNumberErrorMessage}
                                labelText={resources.get("WorkLeaderPhoneNumber")}
                                placeHolder="e.g. +46705555555"
                                onChange={(s) =>
                                    formManager.personFormElementChangeHandler("workLeader", {
                                        ...formState.workLeader,
                                        phoneNumber: s,
                                    })
                                }
                                readOnly={!formManager.canManageContent}
                                inputClassName={formState.workLeader.isActive ? "border border-2 border-success" : ""}
                            />
                        </div>

                        <ParticipantsList
                            onAddParticipant={formManager.addParticipantHandler}
                            onParticipantChange={formManager.updateParticipantHandler}
                            onRemoveParticipant={formManager.removeParticipantHandler}
                            participantListItems={formState.persons}
                            resources={resources}
                            readOnly={!formManager.canManageParticipants}
                        />

                        <div className="row mt-3">
                            <ColumnCheckbox
                                checked={formState.projectLeader.isActive}
                                label={resources.get("OnSite")}
                                onChange={(checked) =>
                                    formManager.personFormElementChangeHandler("projectLeader", {
                                        ...formState.projectLeader,
                                        isActive: checked,
                                    })
                                }
                                inputId="project-leader__is-active"
                                containerClassName="col-1 d-flex flex-column align-items-center"
                                readOnly={!formManager.canManageParticipants}
                            />
                            <TextInput
                                inputId="project-leader__name"
                                containerClassName="col-md-5"
                                value={formState.projectLeader.name}
                                errorMessage={formState.projectLeader.nameErrorMessage}
                                labelText={resources.get("ProjectLeader")}
                                onChange={(s) =>
                                    formManager.personFormElementChangeHandler("projectLeader", {
                                        ...formState.projectLeader,
                                        name: s,
                                    })
                                }
                                readOnly={!formManager.canManageContent}
                                inputClassName={
                                    formState.projectLeader.isActive ? "border border-2 border-success" : ""
                                }
                            />
                            <TextInput
                                inputId="project-leader__phone-number"
                                containerClassName="col-md-5"
                                value={formState.projectLeader.phoneNumber}
                                errorMessage={formState.projectLeader.phoneNumberErrorMessage}
                                labelText={resources.get("ProjectLeaderPhoneNumber")}
                                placeHolder="e.g. +46705555555"
                                onChange={(s) =>
                                    formManager.personFormElementChangeHandler("projectLeader", {
                                        ...formState.projectLeader,
                                        phoneNumber: s,
                                    })
                                }
                                readOnly={!formManager.canManageContent}
                                inputClassName={
                                    formState.projectLeader.isActive ? "border border-2 border-success" : ""
                                }
                            />
                        </div>

                        <LabelCheckbox
                            id="all-involved-have-safe-harbour-education"
                            checked={formState.allInvolvedHaveSafeHarbourEducation.value}
                            label={resources.get("AllInvolvedHaveSafeHarbourEducation")}
                            onChange={(b) => formStateChangeHandler("allInvolvedHaveSafeHarbourEducation", b)}
                            containerClassName="mt-3"
                            readOnly={!formManager.canManageContent}
                        />

                        <div className="mt-3">
                            <input
                                aria-description={resources.get("EducationCheckPerformed")}
                                className={`form-check-input me-1 ${!formManager.canManageContent ? "read-only-check" : ""}`}
                                type="checkbox"
                                checked={formState.educationCheckPerformed.value}
                                onChange={(e) =>
                                    formManager.canManageContent
                                        ? formStateChangeHandler("educationCheckPerformed", e.target.checked)
                                        : false
                                }
                                readOnly={!formManager.canManageContent}
                            />
                            <a href="https://www.sakerhamn.nu/user/Login.aspx" target="_blank">
                                {resources.get("EducationCheckPerformed")}
                            </a>
                        </div>
                        {!formManager.isNewPermit ? (
                            <SignatureDisplay
                                resources={resources}
                                signatureManager={formManager.safeHarbourEducationControlledSignatureManager}
                                signatureLabel={resources.get("SafeHarbourEducationControlledSignature")}
                                containerClassName="mt-3"
                            />
                        ) : null}

                        {showHotWorkAccordion ? (
                            <Accordion className="mt-3" alwaysOpen>
                                {formManager.showHotWorkConditions ? (
                                    <HotWorkConditionsAccordionItem
                                        formState={formState.hotWork}
                                        onChange={formManager.hotWorkChangeHandler}
                                        resources={resources}
                                        baseUserInfo={props.baseUserInfo}
                                        canManageContent={formManager.canManageHotWorkConditions}
                                        hotWorkSection={HotWorkSection.HotWork}
                                    />
                                ) : null}
                                {formManager.showHotWorkCatOneConditions ? (
                                    <HotWorkConditionsAccordionItem
                                        formState={formState.hotWorkCategoryOneConditions}
                                        onChange={formManager.hotWorkChangeHandler}
                                        resources={resources}
                                        baseUserInfo={props.baseUserInfo}
                                        canManageContent={formManager.canManageHotWorkConditions}
                                        hotWorkSection={HotWorkSection.HotWorkCategoryOne}
                                    />
                                ) : null}
                                {formManager.showHotWorkCatTwoConditions ? (
                                    <HotWorkConditionsAccordionItem
                                        formState={formState.hotWorkCategoryTwoConditions}
                                        onChange={formManager.hotWorkChangeHandler}
                                        resources={resources}
                                        baseUserInfo={props.baseUserInfo}
                                        canManageContent={formManager.canManageHotWorkConditions}
                                        hotWorkSection={HotWorkSection.HotWorkCategoryTwo}
                                    />
                                ) : null}
                                {formManager.showHotWorkCatThreeConditions ? (
                                    <HotWorkConditionsAccordionItem
                                        formState={formState.hotWorkCategoryThreeConditions}
                                        onChange={formManager.hotWorkChangeHandler}
                                        resources={resources}
                                        baseUserInfo={props.baseUserInfo}
                                        canManageContent={formManager.canManageHotWorkConditions}
                                        hotWorkSection={HotWorkSection.HotWorkCategoryThree}
                                    />
                                ) : null}
                            </Accordion>
                        ) : null}

                        {showHotWorkSignatures ? (
                            <>
                                {formManager.hotWorkCounterSignatureRequestHandler
                                    .showCounterSignatureRequestSection ? (
                                    <CounterSignatureRequestInput
                                        counterSignatureRequestInputHandler={
                                            formManager.hotWorkCounterSignatureRequestHandler
                                        }
                                        inputId="hot-work-counter-signature-request-input"
                                        resources={resources}
                                        containerClassName="mt-3"
                                    />
                                ) : null}
                                <div className="row mt-3">
                                    <SignatureDisplay
                                        resources={resources}
                                        signatureManager={
                                            formManager.hotWorkInternalDirectorFlammableProductSignatureManager
                                        }
                                        signatureLabel={resources.get("InternalDirectorFlammableProduct")}
                                        containerClassName="me-5"
                                    />
                                    {CounterSignatureRequest_isActive(
                                        formManager.hotWorkCounterSignatureRequestHandler.counterSignatureRequest,
                                    ) ? (
                                        <SignatureDisplay
                                            resources={resources}
                                            signatureManager={
                                                formManager.hotWorkExternalDirectorFlammableProductSignatureManager
                                            }
                                            signatureLabel={resources.get("ExternalDirectorFlammableProduct")}
                                        />
                                    ) : null}
                                </div>
                            </>
                        ) : null}

                        <Accordion className="mt-3" alwaysOpen>
                            <Accordion.Item eventKey="work-conditions-accordion-item">
                                <Accordion.Header>
                                    <span className="fw-bold">{props.resources.get("WorkTerms")}</span>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <div className="row">
                                        <div className="col-4 p-0">
                                            <ProtectiveEquipment
                                                formState={formState.protectiveEquipment}
                                                onChange={formManager.protectiveEquipmentChangeHandler}
                                                resources={props.resources}
                                                canManageContent={formManager.canManageContent}
                                            />
                                        </div>
                                        <div className="col-4 p-0">
                                            <Measures
                                                dbMeasures={storedPermit?.measures}
                                                formState={formState.measures}
                                                onChange={formManager.measuresChangeHandler}
                                                resources={props.resources}
                                                baseUserInfo={props.baseUserInfo}
                                                powerOffDirectorFlammableProductSignatureManager={
                                                    formManager.powerOffDirectorFlammableProductSignatureManager
                                                }
                                                powerOffExternalDirectorFlammableProductSignatureManager={
                                                    formManager.powerOffExternalDirectorFlammableProductSignatureManager
                                                }
                                                powerOffResponsibleElectricianSignatureManager={
                                                    formManager.powerOffResponsibleElectricianSignatureManager
                                                }
                                                powerOffWorkLeaderSignatureManager={
                                                    formManager.powerOffWorkLeaderSignatureManager
                                                }
                                                powerOnDirectorFlammableProductSignatureManager={
                                                    formManager.powerOnDirectorFlammableProductSignatureManager
                                                }
                                                powerOnExternalDirectorFlammableProductSignatureManager={
                                                    formManager.powerOnExternalDirectorFlammableProductSignatureManager
                                                }
                                                powerOnResponsibleElectricianSignatureManager={
                                                    formManager.powerOnResponsibleElectricianSignatureManager
                                                }
                                                powerOnWorkLeaderSignatureManager={
                                                    formManager.powerOnWorkLeaderSignatureManager
                                                }
                                                powerOffPortOfficerSignatureManager={
                                                    formManager.powerOffPortOfficerSignatureManager
                                                }
                                                powerOnPortOfficerSignatureManager={
                                                    formManager.powerOnPortOfficerSignatureManager
                                                }
                                                uiState={formManager.measuresUIState}
                                                permitType={PermitType.EnergyHarbourPermit}
                                                electricFencePowerOffCounterSignatureRequestHandler={
                                                    formManager.electricFencePowerOffCounterSignatureRequestHandler
                                                }
                                                electricFencePowerOnCounterSignatureRequestHandler={
                                                    formManager.electricFencePowerOnCounterSignatureRequestHandler
                                                }
                                                canManageContent={formManager.canManageContent}
                                            />
                                        </div>
                                        <div className="col-4 p-0">
                                            <RiskIdentifications
                                                formState={formState.riskIdentifications}
                                                onChange={formManager.riskIdentificationsChangeHandler}
                                                resources={props.resources}
                                                baseUserInfo={props.baseUserInfo}
                                                craneOperatorSignatureManager={
                                                    formManager.craneOperatorSignatureManager
                                                }
                                                uiState={formManager.riskIdentificationsUIState}
                                                canCreateUnderWaterWorkPermits={
                                                    formManager.canCreateUnderWaterWorkPermits
                                                }
                                                canCreateDivingPermits={formManager.canCreateDivingPermits}
                                                canManageContent={formManager.canManageContent}
                                                dbRiskIdentifications={formManager.storedPermit?.riskIdentifications}
                                            />
                                        </div>
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>

                        {formManager.showAgreementSection ? (
                            <>
                                <Agreements
                                    resources={resources}
                                    permitAgreement={formState.permitAgreement}
                                    participantCount={formManager.participantCount}
                                    baseUserInfo={props.baseUserInfo}
                                    permitAgreementStartDateHandler={formManager.permitAgreementStartDateHandler}
                                    permitAgreementEndDateHandler={formManager.permitAgreementEndDateHandler}
                                    onPermitAgreementChange={formManager.permitAgreementChangeHandler}
                                    permitAgreementPermitProviderSignatureManager={
                                        formManager.permitAgreementPermitProviderSignatureManager
                                    }
                                    permitAgreementPermitSeekerSignatureManager={
                                        formManager.permitAgreementPermitSeekerSignatureManager
                                    }
                                    safetyAgreementPermitSeekerSignatureManager={
                                        formManager.safetyAgreementPermitSeekerSignatureManager
                                    }
                                    canUpdateEndDate={formManager.canUpdatePermitAgreementEndDate}
                                    showUpdateEndDateButton={formManager.showUpdatePermitAgreementEndDateButton}
                                    onUpdateEndDateButtonClick={
                                        formManager.updatePermitAgreementEndDateClickHandlerAsync
                                    }
                                    canManageContent={formManager.canManageContent}
                                    showColdWorkOnly={!!storedPermit && !storedPermit.supportsPositionCategories}
                                />
                            </>
                        ) : null}
                        {formManager.showFinishPermitAccordion && !!storedPermit ? (
                            <FinishPermitAccordion
                                showViewFinishPermitLink={formManager.showFinishPermitLink}
                                lastInSeriesDocumentId={storedPermit.lastPermitInSeriesDocumentId}
                                permitType={PermitType.EnergyHarbourPermit}
                                resources={resources}
                                baseUserInfo={props.baseUserInfo}
                                isFinishedPermitProviderSignatureManager={
                                    formManager.isFinishedPermitProviderSignatureManager
                                }
                                isFinishedPermitSeekerSignatureManager={
                                    formManager.isFinishedPermitSeekerSignatureManager
                                }
                            />
                        ) : null}

                        {formManager.canCreate ? (
                            <Button className="mt-3" type="submit" disabled={formManager.requestInProgress}>
                                {resources.get("Send")}
                            </Button>
                        ) : formManager.canUpdate ? (
                            <Button
                                className="mt-3"
                                type="submit"
                                disabled={formManager.requestInProgress || !formManager.hasUnsavedChanges}
                            >
                                {resources.get("Update")}
                            </Button>
                        ) : formManager.showUpdateHotWorkConditionsButton ? (
                            <Button
                                className="mt-3"
                                type="submit"
                                disabled={formManager.requestInProgress || !formManager.hasUnsavedChanges}
                            >
                                {resources.get("UpdateHotWorkConditions")}
                            </Button>
                        ) : null}

                        <HotWorkCategoryOneOptionsModal
                            formState={formState.hotWorkCategoryOneOptions}
                            onChange={formManager.hotWorkCategoryOneOptionsChangeHandler}
                            onHide={() => formManager.setShowHotWorkCategoryOneOptionsModal(false)}
                            resources={props.resources}
                            show={formManager.showHotWorkCategoryOneOptionsModal}
                            canManageContent={formManager.canManagePermitCategory}
                        />
                        <HotWorkCategoryTwoOptionsModal
                            formState={formState.hotWorkCategoryTwoOptions}
                            onChange={formManager.hotWorkCategoryTwoOptionsChangeHandler}
                            onHide={() => formManager.setShowHotWorkCategoryTwoOptionsModal(false)}
                            resources={props.resources}
                            show={formManager.showHotWorkCategoryTwoOptionsModal}
                            canManageContent={formManager.canManagePermitCategory}
                            version={
                                storedPermit?.hotWorkCategoryTwoOptions.version ??
                                HOT_WORK_CATEGORY_TWO_OPTIONS_VERSION_2026_03
                            }
                        />
                        <HotWorkCategoryThreeOptionsModal
                            formState={formState.hotWorkCategoryThreeOptions}
                            onChange={formManager.hotWorkCategoryThreeOptionsChangeHandler}
                            onHide={() => formManager.setShowHotWorkCategoryThreeOptionsModal(false)}
                            resources={props.resources}
                            show={formManager.showHotWorkCategoryThreeOptionsModal}
                            canManageContent={formManager.canManagePermitCategory}
                        />
                    </form>
                </div>
            </div>
            <AcceptUnsavedChangesModal
                show={formManager.showAcceptUnsavedChangesModal}
                onHide={() => formManager.setShowAcceptUnsavedChangesModal(false)}
                onAccept={formManager.unsavedChangesModalAcceptHandlerAsync}
                onUpdate={formManager.unsavedChangesModalUpdateHandlerAsync}
                resources={resources}
            />
            <SignatureModal
                resources={resources}
                SignatureManager={formManager.safeHarbourEducationControlledSignatureManager}
                ref={formManager.safeHarbourEducationControlledSignatureManager.signaturePadRef}
            />
            <SignatureModal
                resources={resources}
                SignatureManager={formManager.hotWorkInternalDirectorFlammableProductSignatureManager}
                ref={formManager.hotWorkInternalDirectorFlammableProductSignatureManager.signaturePadRef}
            />
            <SignatureModal
                resources={resources}
                SignatureManager={formManager.hotWorkExternalDirectorFlammableProductSignatureManager}
                ref={formManager.hotWorkExternalDirectorFlammableProductSignatureManager.signaturePadRef}
            />
        </>
    );
}

export default EnergyHarbourPermitForm;
